import {ApplicationRef, Component, ElementRef, ErrorHandler, HostBinding, inject, NgZone, OnInit} from "@angular/core";
import {NavigationEnd, Router, RouterOutlet} from "@angular/router";
import {projectListAnimation} from "./core/models/ProjectListAnimation";
import {TranslateService} from "@ngx-translate/core";
import {OverlayContainer} from "@angular/cdk/overlay";
import {ThemesService} from "./core/services/services/themes.service";
import {DomSanitizer, Title} from "@angular/platform-browser";
import {filter, first, map} from "rxjs/operators";
import {MatIconRegistry} from "@angular/material/icon";
import {SwUpdate} from "@angular/service-worker";
import {ConfirmationDialogComponent} from "./shared/dialogs/confirmation-dialog/confirmation-dialog.component";
import {MatDialog, MatDialogRef, MatDialogState} from "@angular/material/dialog";
import {environment} from "../environments/environment";
import {concat, interval, merge, timer} from "rxjs";
import {MessageService} from "./core/services/services/message.service";
import {AlertEnum} from "./core/models/Enums/AlertEnum";
import {AppearanceService} from "src/app/core/services/services/appearance.service";
import {LanguageEnum} from "./core/models/Enums/LanguageEnum";
import {NavigationComponent} from "./core/components/navigation/navigation.component";
import {SidenavComponent} from "./shared/sidenav/sidenav.component";
import {BreadcrumbComponent} from "./core/components/breadcrumb/breadcrumb.component";
import {InformationBannerComponent} from "./core/components/information-banner/information-banner.component";
import {MessageComponent} from "./core/components/message/message.component";
import {CdkScrollable} from "@angular/cdk/scrolling";

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	animations: [projectListAnimation],
	imports: [
		NavigationComponent,
		SidenavComponent,
		BreadcrumbComponent,
		RouterOutlet,
		InformationBannerComponent,
		MessageComponent,
		CdkScrollable,
	],
})
export class AppComponent implements OnInit {
	@HostBinding("class") componentCssClass: string;
	private readonly translate = inject(TranslateService);
	private readonly overlayContainer = inject(OverlayContainer);
	private readonly themeService = inject(ThemesService);
	private readonly elementRef = inject(ElementRef);
	private readonly titleService = inject(Title);
	public readonly router = inject(Router);
	private readonly matIconRegistry = inject(MatIconRegistry);
	private readonly domSanitizer = inject(DomSanitizer);
	private readonly matDialog = inject(MatDialog);
	private readonly errorHandler = inject(ErrorHandler);
	private readonly swUpdate = inject(SwUpdate);
	private readonly appRef = inject(ApplicationRef);
	private readonly ngZone = inject(NgZone);
	private readonly appearanceService = inject(AppearanceService);
	private readonly messageService = inject(MessageService);

	updateDialogRef: MatDialogRef<ConfirmationDialogComponent>;

	constructor() {
		// If in production, check for updates from time to time.
		// Check is done once per minute after the app becomes stable.
		// No check is done if the app isn't visible; if the app becomes visible, a check is done immediately.
		// The first version received is ignored, because since we don't cache anything, the page is up-to-date from the initial load.
		// If an update is received, ask the user to reload the page.

		if (environment.production) {
			this.swUpdate.versionUpdates.subscribe((event) => {
				switch (event.type) {
					case "VERSION_READY":
						this.showUpdateDialog();
						break;
					case "NO_NEW_VERSION_DETECTED":
						break;
					case "VERSION_INSTALLATION_FAILED":
						this.errorHandler.handleError(event.error);
						this.showUpdateDialog();
						break;
				}
			});

			document.addEventListener("visibilitychange", () => {
				if (!document.hidden) {
					this.swUpdate.checkForUpdate();
				}
			});

			// check for updates once per minute, once angular is stable, or after 35 seconds if it's still unstable at that time
			// service-worker gets installed 30 seconds after start if not stable before, this is defined in app.module.ts
			const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
			const after30Seconds$ = timer(30 * 1000);
			const appIsStableOr30Seconds = merge(appIsStable$, after30Seconds$).pipe(first());
			const everyMinute$ = interval(60 * 1000);
			const everyMinuteOnceAppIsStableOr30Seconds$ = concat(appIsStableOr30Seconds, everyMinute$);
			everyMinuteOnceAppIsStableOr30Seconds$.subscribe(() => {
				if (!document.hidden) {
					this.swUpdate.checkForUpdate();
				}
			});
		} else {
			this.ngZone.runOutsideAngular(() => {
				const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
				merge(appIsStable$, timer(20 * 1000))
					.pipe(first())
					.subscribe((v) => {
						// v is true if isStable fired first, or 0 if timer fired first
						if (v !== true && localStorage.getItem("noUnnecessaryPopups") !== "true") {
							this.ngZone.run(() => {
								// eslint-disable-next-line max-len
								this.messageService.add(
									"Application did not become initially stable after 20 seconds. This means there are some pending tasks, possibly created by setInterval or setTimeout. Please make sure there are no long-lasting tasks (e.g. periodic checks with setInterval will NEVER complete). Running them with ngZone.runOutsideAngular (and switching back with ngZone.run if needed) can be a solution here.",
									AlertEnum.WARNING,
									null,
								);
							});
						}
					});
			});
		}

		this.matIconRegistry
			.addSvgIcon("mep_home", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/home_icon.svg"))
			.addSvgIcon(
				"mep_absence_list",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/absence_list_icon.svg"),
			)
			.addSvgIcon(
				"mep_workload_overview",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/workload_overview_icon.svg"),
			)
			.addSvgIcon(
				"mep_trendanalysis_icon_red",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/trendanalysis-icon.svg"),
			)
			.addSvgIcon(
				"mep_attachment_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/attachment_icon.svg"),
			)
			.addSvgIcon(
				"mep_project_report_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/project_report_icon.svg"),
			)
			.addSvgIcon(
				"mep_profile_skills_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/profile_skills_icon.svg"),
			)
			.addSvgIcon(
				"mep_workload_overview_red",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/wo_red_icon.svg"),
			)
			.addSvgIcon("mep_bug_icon", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/bug_icon.svg"))
			.addSvgIcon(
				"mep_project_assignment",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/assignment_icon.svg"),
			)
			.addSvgIcon("mep_person", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/person_icon.svg"))
			.addSvgIcon("mep_profile", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/profile_icon.svg"))
			.addSvgIcon(
				"mep_administration",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/administration_icon.svg"),
			)
			.addSvgIcon("mep_logo", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/mepLogo.svg"))
			.addSvgIcon(
				"mep_reports",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/checklist_icon.svg"),
			)
			.addSvgIcon("mep_search", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/search_icon.svg"))
			.addSvgIcon(
				"mep_resource_search",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/resource-search_icon.svg"),
			)
			.addSvgIcon(
				"mep_skill_search",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/skill-search_icon.svg"),
			)
			.addSvgIcon(
				"mep_actionplan",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/actionplan_icon.svg"),
			)
			.addSvgIcon(
				"mep_add_employee",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/add_employee_icon.svg"),
			)
			.addSvgIcon("mep_add", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/add_icon.svg"))
			.addSvgIcon(
				"mep_edit_skills",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/edit_skills_icon.svg"),
			)
			.addSvgIcon(
				"mep_checklist",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/checklist_icon.svg"),
			)
			.addSvgIcon(
				"mep_clipboard",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/clipboard_icon.svg"),
			)
			.addSvgIcon(
				"mep_copy_move_assignments",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/copy_move_assignments_icon.svg"),
			)
			.addSvgIcon(
				"mep_edit_project_period",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/edit_project_period_icon.svg"),
			)
			.addSvgIcon(
				"mep_favorite",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/favorite_icon.svg"),
			)
			.addSvgIcon("mep_file", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/file_icon.svg"))
			.addSvgIcon("mep_graphic", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/graphic_icon.svg"))
			.addSvgIcon(
				"mep_logout",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/mep_logout_icon.svg"),
			)
			.addSvgIcon(
				"mep_my_projects",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/my_projects_icon.svg"),
			)
			.addSvgIcon(
				"mep_new_page",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/new_page_icon.svg"),
			)
			.addSvgIcon("mep_news", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/news_icon.svg"))
			.addSvgIcon(
				"mep_staffing_request",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/staffing_request_icon.svg"),
			)
			.addSvgIcon(
				"mep_user_group",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/user_group_icon.svg"),
			)
			.addSvgIcon(
				"mep_user_group_2",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/user_group_2_icon.svg"),
			)
			.addSvgIcon(
				"mep_user_info",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/user_info_icon.svg"),
			)
			.addSvgIcon(
				"mep_world",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/world_empty_icon.svg"),
			)
			.addSvgIcon("mep_x", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/x_icon.svg"))
			.addSvgIcon("mep_export", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/export.svg"))
			.addSvgIcon("mep_add_user", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/add_user.svg"))
			.addSvgIcon("mep_filter", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/filter.svg"))
			.addSvgIcon(
				"mep_total_capa",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/total_capa_icon.svg"),
			)
			.addSvgIcon(
				"admin_clipboard_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/admin_clipboard_icon.svg"),
			)
			.addSvgIcon(
				"admin_upload_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/admin_upload_icon.svg"),
			)
			.addSvgIcon(
				"admin_pen_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/admin_pen_icon.svg"),
			)
			.addSvgIcon(
				"admin_cloud_upload_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/admin_cloud_upload_icon.svg"),
			)
			.addSvgIcon(
				"admin_cloud_download_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/admin_cloud_download_icon.svg"),
			)
			.addSvgIcon(
				"admin_contact_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/admin_contact_icon.svg"),
			)
			.addSvgIcon(
				"mep_add_file",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/add_file_icon.svg"),
			)
			.addSvgIcon("mep_reload", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/reload_icon.svg"))
			.addSvgIcon(
				"mep_reload_double",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/reload_double_icon.svg"),
			)
			.addSvgIcon("bell_icon", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/bell_icon.svg"))
			.addSvgIcon(
				"profilemap",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/msg.ProfileMap icon.svg"),
			)
			.addSvgIcon("mep_cockpit", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/cockpit_icon.svg"))
			.addSvgIcon(
				"mep_shield_check",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/shield_check_icon.svg"),
			)
			.addSvgIcon("mep_chatbot", this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/chatbot_icon.svg"))
			.addSvgIcon(
				"mep_teams_icon",
				this.domSanitizer.bypassSecurityTrustResourceUrl("./assets/mepIcons/teams_logo.svg"),
			);
	}

	async ngOnInit() {
		await this.appearanceService.init();

		this.translate.addLangs(["en", "de"]);
		this.translate.setDefaultLang(this.appearanceService.getBrowserLanguage());
		if (this.appearanceService.config.language() !== LanguageEnum.DEFAULT) {
			this.translate.use(this.appearanceService.config.language().toString());
		}

		if (environment.production) {
			(this.elementRef.nativeElement as HTMLElement)?.removeAttribute("ng-version");
		}
		this.themeService.getTheme().then((data) => {
			this.onSetTheme(data + "-theme");
		});

		// Set document title
		const appTitle = this.titleService.getTitle();
		this.router.events
			.pipe(
				filter((routeEvent) => routeEvent instanceof NavigationEnd),
				map(() => this.router.routerState.root),
				map((route) => {
					let child = route.firstChild;
					let lastTitle = child.snapshot.data["title"];
					while (child.firstChild) {
						child = child.firstChild;
						if (child.snapshot.data["title"]) {
							lastTitle = child.snapshot.data["title"];
						}
					}
					return lastTitle;
				}),
			)
			.subscribe((titleKey: string) => {
				if (titleKey) {
					this.translate.get(titleKey).subscribe((title) => {
						if (title === titleKey) {
							this.titleService.setTitle(appTitle + " - " + this.translate.instant(titleKey));
						} else {
							this.titleService.setTitle(appTitle + " - " + title);
						}
					});
				} else {
					// Set default title if no title is provided
					this.titleService.setTitle(appTitle);
				}
			});
	}

	private showUpdateDialog() {
		if (this.updateDialogRef?.getState() === MatDialogState.OPEN) {
			return;
		}

		this.updateDialogRef = this.matDialog.open(ConfirmationDialogComponent, {
			data: "mep.components.general.notification.sw-application-updated",
		});
		this.updateDialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.swUpdate.activateUpdate().then(() => {
					globalThis.location.reload();
				});
			}
		});
	}

	onSetTheme(theme: string): void {
		this.overlayContainer.getContainerElement().classList.add(theme);
		this.componentCssClass = theme;
	}

	/**
	 * Animation
	 */
	getState(outlet: RouterOutlet) {
		return outlet.activatedRouteData.state;
	}
}
